// ONB-001 (Macrofase A) — alta rápida de un artículo: solo nombre y precio. Desde ONB-004 es la variante
// mínima de «Nuevo producto» (Bloque E): usa su motor (`newProduct.ts`) en vez de tener uno propio. Arma el
// mismo borrador que el asistente con las respuestas que el alta rápida no pregunta ya dadas, valida el
// nombre y el precio con sus mismas reglas y manda su mismo cuerpo; el envío y los rechazos son los del
// asistente (`components/product/useProductCreate.ts`). Crea el MISMO artículo que el catálogo de Ventas
// Otros y que vende el punto de venta.
//
// Lo que NO se pregunta queda como lo deja «Nuevo producto» al contestarlo así:
//   - tiendas: «Todas mis tiendas» del dueño (la lista vacía: todas, también las futuras). El alta rápida
//     solo la usa el dueño: el recorrido de inicio y la tarjeta del Inicio son suyos;
//   - «Solo quiero vender» (P-071): sin receta ni costo aproximado — se vende sin tocar inventario y el
//     costo queda sin definir por ahora;
//   - impuestos: «Sin impuestos» (NONE): el precio es lo que se cobra.
//
// Lo único propio de esta variante: el precio tiene que ser mayor que $0. «Nuevo producto» acepta $0, que
// es lo que el producto permite; el alta rápida existe para cobrar la primera venta, y una de $0 no prueba
// nada (decisión de ONB-001, `docs/ONBOARDING_MACRO_A.md`).
import type { BusinessOffering } from '../types/onboarding';
import type { CreateOtherSaleProductInput } from '../types/otherSales';
import { initialDraft, parseAmount, PRODUCT_NAME_MAX, productPayload, stepProblem, type ProductContext, type ProductDraft } from './newProduct';

export interface QuickProductValues { name: string; price: string }
export interface QuickProductErrors { name?: string; price?: string }

/** El alta rápida es del dueño: «Todas mis tiendas» no necesita conocer sus tiendas. */
const OWNER_CONTEXT: ProductContext = { role: 'OWNER', stores: [] };

/** El borrador de «Nuevo producto» con lo que el alta rápida no pregunta ya contestado. */
export function quickDraft(values: QuickProductValues): ProductDraft {
  return { ...initialDraft('OWNER'), name: values.name.trim(), price: values.price, costMode: 'NONE', approximateCost: '', recipeId: null, tax: 'NONE' };
}

export function validateQuickProduct(values: QuickProductValues): QuickProductErrors {
  const errors: QuickProductErrors = {};
  const draft = quickDraft(values);
  if (stepProblem('name', draft, OWNER_CONTEXT)) errors.name = 'Escribe el nombre.';
  else if (draft.name.length > PRODUCT_NAME_MAX) errors.name = `El nombre puede tener hasta ${PRODUCT_NAME_MAX} letras.`;
  if (values.price.trim() === '') errors.price = 'Escribe el precio.';
  else if (stepProblem('price', draft, OWNER_CONTEXT)) errors.price = 'Escribe solo números, con hasta dos decimales (por ejemplo 35 o 35.50).';
  else if (parseAmount(draft.price) <= 0) errors.price = 'El precio tiene que ser mayor que $0.';
  return errors;
}

/** El cuerpo de «Nuevo producto» (`productPayload`) para este borrador. */
export function buildQuickProductPayload(values: QuickProductValues): CreateOtherSaleProductInput {
  return productPayload(quickDraft(values), OWNER_CONTEXT.role, OWNER_CONTEXT.stores);
}

/** Cómo se llama lo que vende, según lo que eligió (o no) al empezar. */
export function offeringNoun(offering: BusinessOffering | null): string {
  if (offering === 'PRODUCTS') return 'producto';
  if (offering === 'SERVICES') return 'servicio';
  return 'producto o servicio';
}

export function offeringExample(offering: BusinessOffering | null): string {
  if (offering === 'PRODUCTS') return 'Ej. Café americano';
  if (offering === 'SERVICES') return 'Ej. Corte de cabello';
  return 'Ej. Café americano o Corte de cabello';
}
